import { ColorMatrixFilter, Container, Sprite, Text, type Texture } from 'pixi.js';
import type { SlotTrailState, TrailSquare } from '@goodtime/shared';
import { AudioBus } from '../../audio/AudioBus';
import { trailBonusLead, trailPrizeText } from '../../logic/trail/trail';
import { BoardDialog } from '../board/BoardDialog';
import { BrassButton } from '../board/BrassButton';
import { animate, COLOURS, displayStyle, ease, uiStyle } from '../board/ui';
import { glowTexture } from '../road/glow';

/** What the bonus is drawn with: the walnut tile each box sits on, the
 *  slot's own box (its scatter symbol), the prizes' icons and the floor. */
export interface TrailBonusArt {
  (name: 'tile' | 'box' | 'coin' | 'gem' | 'floor'): Texture | null;
}

/** What the bonus asks of its host. */
export interface TrailBonusDeps {
  /** Open box `index` on the server: the trail after it, and the coins in
   *  the wallet then (null if unknown). */
  pick(index: number): Promise<{ trail: SlotTrailState; coins: number | null }>;
  /** Coins won fly from `from` (canvas pixels) into the coin counter,
   *  adding to it as they land. */
  flyCoins(from: { x: number; y: number }, amount: number): Promise<void>;
  /** The counter takes the server's figure once they have landed. */
  syncCoins(coins: number): void;
  /** The board's title (BONUS) and its last button (the next level's
   *  bigger prizes): the road's gift square has its own, as it moves no
   *  level on. */
  title?: string;
  finishLabel?: string;
}

/** The bonus as plain data, for browser tests. */
export interface TrailBonusSnapshot {
  open: boolean;
  lead: string;
  /** Each box: "Box 3", or what it held ("+500 coins", "+2 gems"). */
  boxes: string[];
  note: string;
  error: string;
  buttons: string[];
}

interface Box {
  root: Container;
  body: Container;
  glow: Sprite;
  tile: Sprite;
  face: Container;
  shown: TrailSquare | null;
}

const GAP = 10;

/**
 * The pick-a-prize bonus at the end of a Sweet Trail, on a board (producer
 * 2026-10-14: painted, in Pixi): pick 3 of 9 boxes, each the slot's own box
 * on a walnut tile. The server holds what's in them and pays each pick; a
 * box of coins sends them flying into the coin counter. The last pick shows
 * every box and moves the trail up a level.
 */
export class TrailBonusDialog extends BoardDialog {
  private readonly lead: Text;
  private readonly grid = new Container();
  private readonly note: Text;
  private readonly error: Text;
  private readonly next: BrassButton;
  private readonly later: BrassButton;
  private readonly dimmed = new ColorMatrixFilter();
  private boxes: Box[] = [];
  private trail: SlotTrailState | null = null;
  private deps: TrailBonusDeps | null = null;
  private busy: number | null = null;
  private payouts: Promise<void> = Promise.resolve();
  private finishing = false;
  private done: ((trail: SlotTrailState) => void) | null = null;
  private laidOutWidth = 0;

  constructor(private readonly art: TrailBonusArt) {
    super({ title: 'BONUS', floor: art('floor'), maxWidth: 380, titleSize: 19 });
    this.lead = new Text({ text: '', style: displayStyle(22) });
    this.lead.anchor.set(0.5, 0);
    this.note = new Text({ text: 'Every box has a prize', style: { ...uiStyle(15, 0xf0dcb4, '500'), align: 'center' } });
    this.note.anchor.set(0.5, 0);
    this.error = new Text({ text: '', style: { ...uiStyle(14, COLOURS.loss, '600'), align: 'center', wordWrap: true } });
    this.error.anchor.set(0.5, 0);
    this.next = new BrassButton({ label: 'LEVEL 2: BIGGER PRIZES', kind: 'gold', size: 17, onTap: () => void this.finish() });
    this.later = new BrassButton({ label: 'LATER', kind: 'wood', size: 17, onTap: () => void this.finish() });
    this.dimmed.saturate(-0.5);
    this.dimmed.brightness(0.7, true);
    this.content.addChild(this.lead, this.grid, this.note, this.error, this.next, this.later);
  }

  /** Play the bonus from `trail`; resolves with the trail as it ends when
   *  the board closes (the bonus finished, or left for later after a
   *  failed pick: the gift still waits on the trail). */
  play(trail: SlotTrailState, deps: TrailBonusDeps): Promise<SlotTrailState> {
    this.trail = trail;
    this.deps = deps;
    this.busy = null;
    this.error.text = '';
    this.setTitle(deps.title ?? 'BONUS');
    this.setFloor(this.art('floor'));
    this.buildBoxes(trail.bonus?.boxes ?? 9);
    this.render(false);
    AudioBus.shared.play('ui:modal_open', { volume: 0.5 });
    void this.open();
    return new Promise((resolve) => (this.done = resolve));
  }

  override layout(viewWidth: number, viewHeight: number): void {
    super.layout(viewWidth, viewHeight);
    if (this.trail && this.contentWidth !== this.laidOutWidth) this.render(false);
  }

  get snapshot(): TrailBonusSnapshot {
    return {
      open: this.visible,
      lead: this.lead.text,
      boxes: this.boxes.map((b, i) => (b.shown ? `${trailPrizeText(b.shown)} ${b.shown.kind === 'GEMS' ? 'gems' : 'coins'}` : `Box ${i + 1}`)),
      note: this.note.visible ? this.note.text : '',
      error: this.error.text,
      buttons: [this.later, this.next].filter((b) => b.visible).map((b) => b.labelText),
    };
  }

  /** Tap box `index` as a player does (browser tests). */
  tapBox(index: number): boolean {
    if (!this.visible || index < 0 || index >= this.boxes.length) return false;
    void this.pick(index);
    return true;
  }

  /** Press a button by its label (browser tests). */
  press(label: string): boolean {
    const button = [this.next, this.later].find((b) => b.visible && b.labelText.startsWith(label));
    if (!button) return false;
    button.onTap?.();
    return true;
  }

  /** Where a box or button is on the canvas (browser tests). */
  where(target: number | string): { x: number; y: number } | null {
    const node = typeof target === 'number' ? this.boxes[target]?.root : [this.next, this.later].find((b) => b.visible && b.labelText.startsWith(target));
    if (!node || !this.visible) return null;
    const b = node.getBounds();
    return { x: b.x + b.width / 2, y: b.y + b.height / 2 };
  }

  // ---- the game -------------------------------------------------------

  private async pick(index: number): Promise<void> {
    const trail = this.trail;
    const bonus = trail?.bonus;
    const box = this.boxes[index];
    if (!trail || !bonus || !box || !this.deps || this.busy !== null || bonus.finished || box.shown) return;
    if (bonus.picks.some((p) => p.index === index)) return;
    this.busy = index;
    this.error.text = '';
    AudioBus.shared.play('ui:click', { volume: 0.6 });
    // The box rattles while the server opens it.
    const wobble = (): void => {
      if (this.destroyed || box.root.destroyed) return;
      if (this.busy !== index) {
        box.body.rotation = 0;
        return;
      }
      box.body.rotation = Math.sin(performance.now() / 60) * 0.05;
      requestAnimationFrame(wobble);
    };
    requestAnimationFrame(wobble);
    try {
      const { trail: after, coins } = await this.deps.pick(index);
      this.trail = after;
      this.busy = null;
      box.body.rotation = 0;
      const picked = after.bonus?.picks.find((p) => p.index === index) ?? null;
      if (picked) {
        await this.reveal(box, picked, true);
        void this.celebrate({ x: this.grid.x + box.root.x, y: this.grid.y + box.root.y });
        AudioBus.shared.play('win:sting_small', { volume: 0.6 });
        if (picked.kind === 'COINS' && picked.amount > 0) {
          const from = box.root.getGlobalPosition();
          const deps = this.deps;
          this.payout(async () => {
            await deps.flyCoins(from, picked.amount);
            if (coins !== null) deps.syncCoins(coins);
          });
        } else if (coins !== null) {
          const deps = this.deps;
          this.payout(() => deps.syncCoins(coins));
        }
      }
      if (after.bonus?.finished) {
        AudioBus.shared.play('bonus:claim', { volume: 0.8 });
        await this.revealRest(after);
      }
      this.render(true);
    } catch {
      this.busy = null;
      box.body.rotation = 0;
      this.error.text = "Couldn't open that box. Try again.";
      this.render(true);
    }
  }

  /** Every box not picked shows what it held, a little faded. */
  private async revealRest(trail: SlotTrailState): Promise<void> {
    const prizes = trail.bonus?.prizes ?? [];
    const waits: Promise<void>[] = [];
    this.boxes.forEach((box, i) => {
      const prize = prizes[i];
      if (box.shown || !prize) return;
      waits.push(new Promise((r) => setTimeout(r, 90 * waits.length)).then(() => this.reveal(box, prize, false)));
    });
    await Promise.all(waits);
  }

  /** Turn a box over to show `prize`: picked boxes glow, the rest fade. */
  private async reveal(box: Box, prize: TrailSquare, picked: boolean): Promise<void> {
    await animate(150, (t) => box.body.scale.set(1 - t, 1));
    box.shown = prize;
    this.drawFace(box, prize, picked);
    await animate(220, (t) => box.body.scale.set(ease.outBack(t), 1));
    if (picked) {
      box.glow.visible = true;
      void animate(420, (t) => {
        box.glow.alpha = 0.85 * Math.min(1, t * 2);
      });
    }
  }

  private async finish(): Promise<void> {
    const trail = this.trail;
    if (!trail || this.finishing) return;
    this.finishing = true;
    this.next.setEnabled(false);
    this.later.setEnabled(false);
    // Coins still flying land first (the board never waits long for them:
    // any left finish over the slot).
    await Promise.race([this.payouts, new Promise((r) => setTimeout(r, 2500))]);
    await this.close();
    this.next.setEnabled(true);
    this.later.setEnabled(true);
    const done = this.done;
    this.done = null;
    this.finishing = false;
    done?.(trail);
  }

  /** Pay out in order (coins flying, then the counter set to the
   *  server's figure); a payout that fails never holds up the next. */
  private payout(step: () => Promise<void> | void): void {
    this.payouts = this.payouts.then(step).catch((err: unknown) => console.warn('[trail bonus] a payout failed', err));
  }

  // ---- drawing --------------------------------------------------------

  private buildBoxes(count: number): void {
    this.grid.removeChildren().forEach((c) => c.destroy({ children: true }));
    this.boxes = Array.from({ length: count }, (_, i) => {
      const root = new Container();
      const body = new Container();
      const glow = new Sprite(glowTexture());
      glow.anchor.set(0.5);
      glow.tint = 0xffd36b;
      glow.blendMode = 'add';
      glow.visible = false;
      const tile = new Sprite();
      tile.anchor.set(0.5);
      const face = new Container();
      body.addChild(tile, face);
      root.addChild(glow, body);
      root.eventMode = 'static';
      root.cursor = 'pointer';
      root.label = `trail-box-${i}`;
      root.on('pointertap', () => void this.pick(i));
      const box: Box = { root, body, glow, tile, face, shown: null };
      this.grid.addChild(root);
      return box;
    });
    // Boxes already opened (a bonus left half done) show what they held.
    for (const p of this.trail?.bonus?.picks ?? []) {
      const box = this.boxes[p.index];
      if (box) box.shown = p;
    }
  }

  private render(keepFaces: boolean): void {
    const trail = this.trail;
    if (!trail) return;
    const bonus = trail.bonus;
    const finished = bonus?.finished ?? false;
    const w = this.contentWidth;
    this.laidOutWidth = w;
    this.lead.text = trailBonusLead(trail);
    this.lead.position.set(w / 2, 0);
    const size = Math.floor(Math.min(96, (w - GAP * 2) / 3));
    const picks = new Set((bonus?.picks ?? []).map((p) => p.index));
    this.boxes.forEach((box, i) => {
      box.root.position.set((i % 3) * (size + GAP) + size / 2, Math.floor(i / 3) * (size + GAP) + size / 2);
      const tile = this.art('tile');
      if (tile) box.tile.texture = tile;
      box.tile.width = box.tile.height = size;
      box.glow.width = box.glow.height = size * 1.5;
      if (!keepFaces || box.face.children.length === 0) this.drawFace(box, box.shown, picks.has(i));
      box.glow.visible = box.shown !== null && picks.has(i);
      box.glow.alpha = 0.85;
      box.root.cursor = !box.shown && !finished ? 'pointer' : 'default';
    });
    const gridW = size * 3 + GAP * 2;
    const rows = Math.ceil(this.boxes.length / 3);
    this.grid.position.set(Math.round((w - gridW) / 2), this.lead.height + 14);
    let y = this.grid.y + rows * size + (rows - 1) * GAP + 14;
    this.error.style.wordWrapWidth = w;
    this.error.visible = this.error.text !== '';
    if (this.error.visible) {
      this.error.position.set(w / 2, y);
      y += this.error.height + 10;
    }
    this.next.visible = finished;
    this.later.visible = !finished && this.error.visible;
    this.note.visible = !finished && !this.later.visible;
    if (finished) {
      this.next.setLabel(this.deps?.finishLabel ?? `LEVEL ${trail.level}: BIGGER PRIZES`);
      this.next.resize(Math.min(w, 300), 50);
      this.next.position.set(Math.round((w - this.next.buttonWidth) / 2), y + 2);
      y += 56;
    } else if (this.later.visible) {
      this.later.resize(Math.min(w, 180), 46);
      this.later.position.set(Math.round((w - this.later.buttonWidth) / 2), y + 2);
      y += 52;
    } else {
      this.note.position.set(w / 2, y);
      y += this.note.height + 2;
    }
    this.setContentHeight(y);
  }

  /** A box's face: the slot's box while closed; once open, its prize. */
  private drawFace(box: Box, prize: TrailSquare | null, picked: boolean): void {
    box.face.removeChildren().forEach((c) => c.destroy({ children: true }));
    const size = box.tile.height || 90;
    box.body.filters = prize && !picked ? [this.dimmed] : [];
    if (!prize) {
      const art = this.art('box');
      if (art) {
        const s = new Sprite(art);
        s.anchor.set(0.5);
        const k = (size * 0.74) / Math.max(art.width, art.height);
        s.scale.set(k);
        box.face.addChild(s);
      }
      return;
    }
    const iconTexture = prize.kind === 'GEMS' ? this.art('gem') : this.art('coin');
    if (iconTexture) {
      const icon = new Sprite(iconTexture);
      icon.anchor.set(0.5);
      icon.width = icon.height = Math.round(size * 0.42);
      icon.y = -size * 0.12;
      box.face.addChild(icon);
    }
    const amount = new Text({ text: trailPrizeText(prize), style: displayStyle(Math.max(14, Math.round(size * 0.2))) });
    amount.anchor.set(0.5, 0.5);
    amount.y = size * 0.24;
    if (amount.width > size * 0.86) amount.scale.set((size * 0.86) / amount.width);
    box.face.addChild(amount);
  }
}
